
import { defineComponent } from 'vue'
import { iContent, iGlossary, iNode, iWord } from './GlossaryDefinition'
import { AxiosResponse } from 'axios'
import Card from 'primevue/card'
import Dropdown from 'primevue/dropdown'
import Message from 'primevue/message'
import glossaryDefinitionDescriptor from './GlossaryDefinitionDescriptor.json'
import GlossaryDefinitionHint from './GlossaryDefinitionHint.vue'
import GlossaryDefinitionNodeDialog from './dialogs/GlossaryDefinitionNodeDialog.vue'
import FabButton from '@/components/UI/KnFabButton.vue'
import Tree from 'primevue/tree'

export default defineComponent({
    name: 'glossary-definition-detail',
    components: {
        Card,
        Dropdown,
        GlossaryDefinitionHint,
        GlossaryDefinitionNodeDialog,
        FabButton,
        Message,
        Tree
    },
    props: { reloadTree: { type: Boolean } },
    emits: ['addWord', 'infoClicked'],
    data() {
        return {
            glossaryDefinitionDescriptor,
            glossaries: [] as iGlossary[],
            selectedGlossaryId: null as number | null,
            selectedGlossary: null as iGlossary | null,
            originalGlossary: null as iGlossary | null,
            nodes: [] as iNode[],
            buttonVisible: [],
            searchWord: null,
            timer: null as any,
            expandedKeys: {},
            nodeDialogVisible: false,
            selectedContent: {} as iContent,
            selectedNode: {} as iNode,
            dropzoneActive: [] as boolean[],
            showTree: false,
            showHint: true,
            loading: false
        }
    },
    watch: {
        async reloadTree() {
            this.updateParentNode('HAVE_WORD_CHILD', true)
            await this.listContents(this.selectedGlossaryId as number, this.selectedNode)
        }
    },
    async created() {
        await this.loadGlossaryList()
    },
    methods: {
        async loadGlossaryInfo(glossaryId: number, parent: any) {
            await this.loadGlossary(glossaryId)
            await this.listContents(glossaryId, parent)
        },
        async loadGlossaryList() {
            await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/glossary/listGlossary`).then((response: AxiosResponse<any>) => (this.glossaries = response.data))
        },
        async loadGlossary(glossaryId: number) {
            this.loading = true
            await this.$http
                .get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/glossary/getGlossary?GLOSSARY_ID=${glossaryId}`)
                .then((response: AxiosResponse<any>) => {
                    this.selectedGlossary = { ...response.data, SaveOrUpdate: 'Update' }
                    this.originalGlossary = { ...response.data, SaveOrUpdate: 'Update' }
                    this.showTree = true
                    this.showHint = false
                })
                .finally(() => (this.loading = false))
        },
        async listContents(glossaryId: number, parent: any) {
            this.loading = true

            if (parent?.WORD_ID || this.searchWord) {
                this.loading = false
                return
            }

            const parentId = parent ? parent.id : null
            let content = [] as iNode[]
            await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/glossary/listContents?GLOSSARY_ID=${glossaryId}&PARENT_ID=${parentId}`).then((response: AxiosResponse<any>) => {
                response.data.forEach((el: any) => content.push(this.createNode(el, parent)))
                content.sort((a: iNode, b: iNode) => (a.label > b.label ? 1 : -1))
            })
            this.attachContentToTree(parent, content)
            this.loading = false
        },
        attachContentToTree(parent: iNode, content: iNode[]) {
            if (parent) {
                parent.children = []
                parent.children = content
            } else {
                this.nodes = []
                this.nodes = content
            }
        },
        createNode(el: any, parent: any) {
            return {
                key: el.CONTENT_ID ? `content-${el.CONTENT_ID}` : `word-${el.WORD_ID}`,
                id: el.CONTENT_ID ?? el.WORD_ID,
                label: el.CONTENT_NM ?? el.WORD,
                icon: el.WORD_ID && !(el.HAVE_WORD_CHILD || el.HAVE_CONTENTS_CHILD) ? 'pi pi-circle-on' : '',
                children: [] as iNode[],
                data: el,
                style: this.glossaryDefinitionDescriptor.node.style,
                leaf: !(el.HAVE_WORD_CHILD || el.HAVE_CONTENTS_CHILD),
                selectable: !(el.HAVE_WORD_CHILD || el.HAVE_CONTENTS_CHILD),
                parent: parent
            }
        },
        async filterGlossaryTree() {
            if (this.timer) {
                clearTimeout(this.timer)
                this.timer = null
            }
            let tempData = []
            this.timer = setTimeout(() => {
                this.loading = true
                this.$http
                    .get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/glossary/glosstreeLike?WORD=${this.searchWord}&GLOSSARY_ID=${this.selectedGlossary?.GLOSSARY_ID}`)
                    .then((response: AxiosResponse<any>) => (tempData = response.data))
                    .finally(() => {
                        this.createGlossaryTree(tempData)
                        this.loading = false
                    })
            }, 1000)
        },
        createGlossaryTree(data: any) {
            this.nodes = []
            this.expandedKeys = {}
            data.GlossSearch.SBI_GL_CONTENTS.forEach((el: any) => {
                const tempNode = this.createNode(el, null)
                el.CHILD?.forEach((el: any) => {
                    tempNode.children.push(this.createNode(el, tempNode))
                })
                this.nodes.push(tempNode)
            })
        },
        async saveWordConfirm(event: any, item: any) {
            if (item.data.HAVE_CONTENTS_CHILD || item.data.WORD_ID) {
                return
            }

            const word = JSON.parse(event.dataTransfer.getData('text/plain'))
            this.$confirm.require({
                message: this.$t('managers.glossary.glossaryDefinition.saveWordConfirmMessage'),
                header: this.$t('managers.glossary.glossaryDefinition.saveWordConfirmTitle'),
                icon: 'pi pi-exclamation-triangle',
                accept: async () => await this.saveWord(word, item)
            })
        },
        async saveWord(word: iWord, item: any) {
            this.loading = true
            this.selectedNode = item
            await this.$http
                .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + '1.0/glossary/business/addContents', {
                    GLOSSARY_ID: this.selectedGlossaryId,
                    PARENT_ID: item.id,
                    WORD_ID: word.WORD_ID
                })
                .then(async (response: AxiosResponse<any>) => {
                    if (response.data.Status !== 'NON OK') {
                        this.$store.commit('setInfo', {
                            title: this.$t('common.toast.createTitle'),
                            msg: this.$t('common.toast.success')
                        })
                        this.updateParentNode('HAVE_WORD_CHILD', true)
                        await this.listContents(this.selectedGlossaryId as number, item)
                    } else {
                        this.$store.commit('setError', {
                            title: this.$t('common.error.generic'),
                            msg: this.$t(this.glossaryDefinitionDescriptor.translation[response.data.Message])
                        })
                    }
                })
                .catch((response) => {
                    this.$store.commit('setError', {
                        title: this.$t('common.error.generic'),
                        msg: response
                    })
                })
                .finally(() => {
                    this.loading = false
                    this.dropzoneActive = []
                })
        },
        async deleteNodeConfirm(node: any) {
            this.$confirm.require({
                message: this.$t('managers.glossary.glossaryDefinition.deleteNodeConfirmMessage'),
                header: this.$t('common.toast.deleteTitle'),
                icon: 'pi pi-exclamation-triangle',
                accept: async () => await this.deleteNode(node)
            })
        },
        async deleteNode(node: any) {
            this.loading = true
            this.selectedNode = node.parent
            const url = node.data.CONTENT_ID ? `1.0/glossary/business/deleteContents?CONTENTS_ID=${node.data.CONTENT_ID}` : `1.0/glossary/business/deleteContents?PARENT_ID=${node.parent.id}&WORD_ID=${node.data.WORD_ID}`
            let status = ''
            await this.$http
                .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + url, {})
                .then((response: AxiosResponse<any>) => (status = response.data.Status))
                .catch((response) => {
                    this.$store.commit('setError', {
                        title: this.$t('common.error.generic'),
                        msg: response
                    })
                })

            if (status === 'OK') {
                let property = 'HAVE_CONTENTS_CHILD'
                if (node.data.WORD_ID) {
                    property = 'HAVE_WORD_CHILD'
                }

                this.searchWord ? await this.filterGlossaryTree() : await this.listContents(this.selectedGlossaryId as number, node.parent)
                if (this.selectedNode.children.length === 0) {
                    this.updateParentNode(property, false)
                }
            }

            this.dropzoneActive = []
            this.loading = false
        },
        async showNodeDialog(node: any, mode: string) {
            this.selectedNode = node
            if (mode === 'edit') {
                await this.loadContent(node.data.CONTENT_ID)
            } else {
                this.selectedContent = {
                    CONTENT_ID: '',
                    CONTENT_NM: '',
                    CONTENT_CD: '',
                    CONTENT_DS: '',
                    GLOSSARY_ID: this.selectedGlossaryId as number,
                    NEWCONT: true,
                    PARENT_ID: node ? node.data.CONTENT_ID : null,
                    SaveOrUpdate: 'Save'
                }
            }
            this.nodeDialogVisible = true
        },
        async loadContent(contentId: number) {
            this.loading = true
            await this.$http
                .get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/glossary/getContent?CONTENT_ID=${contentId}`)
                .then(
                    (response: AxiosResponse<any>) =>
                        (this.selectedContent = {
                            ...response.data,
                            CONTENT_ID: contentId,
                            SaveOrUpdate: 'Update'
                        })
                )
                .finally(() => (this.loading = false))
        },
        async saveContent(content: iContent) {
            this.loading = true

            let result = { status: '', message: '' } as any
            await this.$http
                .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + '1.0/glossary/business/addContents', content)
                .then(
                    (response: AxiosResponse<any>) =>
                        (result = {
                            status: response.data.Status,
                            message: response.data.Message
                        })
                )
                .catch((response) => {
                    this.$store.commit('setError', {
                        title: this.$t('common.error.generic'),
                        msg: response
                    })
                })

            await this.updateTree(result, content)
            this.loading = false
        },
        async updateTree(result: { status: string; message: string }, content: iContent) {
            if (result.status === 'NON OK') {
                this.$store.commit('setError', {
                    title: this.$t('common.toast.createTitle'),
                    msg: this.$t(this.glossaryDefinitionDescriptor.translation[result.message])
                })
            } else {
                this.$store.commit('setInfo', {
                    title: this.$t('common.toast.createTitle'),
                    msg: this.$t('common.toast.success')
                })
                this.nodeDialogVisible = false
                this.updateParentNode('HAVE_CONTENTS_CHILD', true)
                content.SaveOrUpdate === 'Save' ? await this.listContents(this.selectedGlossaryId as number, this.selectedNode) : this.updateNode(content)
            }
        },
        updateNode(content: iContent) {
            let temp = null as any
            for (let i = 0; i < this.nodes.length; i++) {
                temp = this.findNode(this.nodes[i], this.selectedNode?.id)
                if (temp) break
            }

            if (temp) {
                temp.data = content
                temp.label = content.CONTENT_NM
            }
        },
        findNode(node: iNode, nodeId: number) {
            if (node.id === nodeId) {
                return node
            } else if (node.children != null) {
                let result = null as any
                for (let i = 0; result == null && i < node.children.length; i++) {
                    result = this.findNode(node.children[i], nodeId)
                }
                return result
            }
            return null
        },
        deleteGlossaryConfirm() {
            this.$confirm.require({
                message: this.$t('common.toast.deleteMessage'),
                header: this.$t('common.toast.deleteTitle'),
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.deleteGlossary()
                }
            })
        },
        async deleteGlossary() {
            this.loading = true
            await this.$http.post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/glossary/business/deleteGlossary?GLOSSARY_ID=${this.selectedGlossaryId}`).then(() => {
                this.$store.commit('setInfo', {
                    title: this.$t('common.toast.deleteTitle'),
                    msg: this.$t('common.toast.deleteSuccess')
                })
                this.selectedGlossaryId = null
                this.selectedGlossary = null
                this.originalGlossary = null
            })
            await this.loadGlossaryList()
            this.loading = false
        },
        addWord(node: iNode) {
            this.selectedNode = node
            this.$emit('addWord', {
                parent: node.data,
                glossaryId: this.selectedGlossaryId
            })
        },
        async addNewGlossary(type: string) {
            this.showTree = false
            this.showHint = false
            this.selectedGlossaryId = null
            this.expandedKeys = {}

            if (type === 'Save') {
                this.nodes = []
                this.selectedGlossary = {
                    GLOSSARY_CD: '',
                    GLOSSARY_DS: '',
                    GLOSSARY_NM: '',
                    NEWGLOSS: true,
                    SBI_GL_CONTENTS: [],
                    SaveOrUpdate: 'Save'
                }
            } else {
                this.selectedGlossary = {
                    GLOSSARY_ID: this.selectedGlossary?.GLOSSARY_ID,
                    GLOSSARY_CD: this.selectedGlossary?.GLOSSARY_CD,
                    GLOSSARY_DS: this.selectedGlossary?.GLOSSARY_DS,
                    GLOSSARY_NM: this.$t('common.copyOf') + ' ' + this.selectedGlossary?.GLOSSARY_NM
                } as iGlossary
                await this.handleSaveGlossary()
            }

            this.originalGlossary = {
                GLOSSARY_CD: '',
                GLOSSARY_DS: '',
                GLOSSARY_NM: ''
            } as iGlossary
        },
        async handleSaveGlossary() {
            this.loading = true

            if (!this.selectedGlossary?.GLOSSARY_NM || !this.glossaryChanged()) {
                this.loading = false
                return
            }

            const url = this.selectedGlossary?.SaveOrUpdate ? '1.0/glossary/business/addGlossary' : '1.0/glossary/business/cloneGlossary'
            let tempData = {} as any
            await this.$http
                .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + url, this.selectedGlossary)
                .then((response: AxiosResponse<any>) => {
                    tempData = response.data
                })
                .catch((response) => {
                    this.$store.commit('setError', {
                        title: this.$t('common.error.generic'),
                        msg: response
                    })
                })

            this.updateGlossaryList(tempData)
            this.loading = false
        },
        async updateGlossaryList(tempData: any) {
            if (tempData.Status && tempData.Status !== 'NON OK') {
                this.$store.commit('setInfo', {
                    title: this.$t('common.toast.createTitle'),
                    msg: this.$t('common.toast.success')
                })
                if (this.selectedGlossary?.SaveOrUpdate === 'Update') {
                    this.updateGlossary()
                } else {
                    if (tempData.id && this.selectedGlossary) {
                        this.selectedGlossary.GLOSSARY_ID = tempData.id
                        this.selectedGlossaryId = tempData.id
                        if (this.selectedGlossaryId) await this.loadGlossaryInfo(this.selectedGlossaryId, null)
                    }
                    await this.loadGlossaryList()
                }
                this.showTree = true
                if (this.selectedGlossary) {
                    this.selectedGlossary.SaveOrUpdate = 'Update'
                }
                this.originalGlossary = { ...this.selectedGlossary } as iGlossary
            } else {
                this.$store.commit('setError', {
                    title: this.$t('common.error.generic'),
                    msg: this.glossaryDefinitionDescriptor.translation[tempData.Message] ? this.$t(this.glossaryDefinitionDescriptor.translation[tempData.Message]) : ''
                })
            }
        },
        glossaryChanged() {
            return this.selectedGlossary?.GLOSSARY_NM !== this.originalGlossary?.GLOSSARY_NM || this.selectedGlossary?.GLOSSARY_CD !== this.originalGlossary?.GLOSSARY_CD || this.selectedGlossary?.GLOSSARY_DS !== this.originalGlossary?.GLOSSARY_DS
        },
        updateGlossary() {
            const index = this.glossaries.findIndex((el: iGlossary) => el.GLOSSARY_ID === this.selectedGlossary?.GLOSSARY_ID)
            this.glossaries[index] = this.selectedGlossary as iGlossary
        },
        updateGlossaryName(newName: any) {
            if (this.selectedGlossary) {
                this.selectedGlossary.GLOSSARY_NM = newName
            }
        },
        updateParentNode(property: string, value: any) {
            let temp = null as any
            if (this.selectedNode) {
                for (let i = 0; i < this.nodes.length; i++) {
                    temp = this.findNode(this.nodes[i], this.selectedNode.id)
                    if (temp) break
                }
            }

            if (temp) {
                temp.data[property] = value
            }
        },
        setDropzoneClass(value: boolean, node: any) {
            if (node.data.CONTENT_ID && !node.data.HAVE_CONTENTS_CHILD) {
                this.dropzoneActive[node.key] = value
            }
        }
    }
})
